import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Sidebar from "../../components/layout/Sidebar";
import {
  ArrowLeft,
  Cpu,
  HardDrive,
  Network,
  Cloud,
  Server,
  Laptop,
  Shield,
  Wrench,
  Phone,
  Mail,
  MapPin,
  CheckCircle,
  FileCheck,
} from "lucide-react";

const ICTSystems = () => {
  type TabId = "overview" | "solutions" | "configuration" | "support" | "faq" | "portfolio";
  const [activeTab, setActiveTab] = useState<TabId>("overview");

  const pageName = "ICT Systems";
  const title = `${pageName} | Northstart Systems`;
  const description =
    "End-to-end ICT infrastructure services in Kenya: hardware procurement, software licensing, networks, servers, cloud integration, and managed support.";
  const canonical = "https://northstartsystems.com/services/ict"; // update to live URL

  const heroBadges = [
    { label: "Enterprise-Ready", icon: <Server className="w-4 h-4 mr-2" /> },
    { label: "Cloud & On-Prem", icon: <Cloud className="w-4 h-4 mr-2" /> },
    { label: "Secure by Design", icon: <Shield className="w-4 h-4 mr-2" /> },
    { label: "Vendor Certified", icon: <FileCheck className="w-4 h-4 mr-2" /> },
  ];

  const solutions = [
    {
      icon: <Cpu className="w-8 h-8 text-purple-400" />,
      title: "Hardware Procurement",
      desc: "Sourcing desktops, laptops, printers, switches, and enterprise hardware.",
      bullets: [
        "Workstations & business laptops (Dell, HP, Lenovo)",
        "Enterprise switches/routers (Cisco, MikroTik, Ubiquiti)",
        "Printers, scanners, POS systems",
        "Warranty & vendor-certified gear",
      ],
    },
    {
      icon: <FileCheck className="w-8 h-8 text-indigo-400" />,
      title: "Software Licensing",
      desc: "OEM and volume licensing for productivity, OS, and specialized software.",
      bullets: [
        "Microsoft 365 & Windows licensing",
        "Antivirus & EDR solutions",
        "Industry-specific software licensing",
        "Subscription and license tracking",
      ],
    },
    {
      icon: <Network className="w-8 h-8 text-blue-400" />,
      title: "Network Setup",
      desc: "LAN, Wi-Fi, firewalls, and structured cabling for offices and campuses.",
      bullets: [
        "Structured cabling (Cat6/Cat6A, Fiber)",
        "Firewall and VPN configuration",
        "Enterprise Wi-Fi with roaming",
        "Network monitoring & troubleshooting",
      ],
    },
    {
      icon: <Server className="w-8 h-8 text-green-400" />,
      title: "Server Installation",
      desc: "Windows/Linux servers, virtualization, and storage configuration.",
      bullets: [
        "Domain controllers & Active Directory",
        "File & application servers",
        "Virtualization (VMware, Hyper-V, Proxmox)",
        "Backup & disaster recovery solutions",
      ],
    },
    {
      icon: <Cloud className="w-8 h-8 text-cyan-400" />,
      title: "Cloud & Hybrid Solutions",
      desc: "Migration and integration with cloud platforms for agility and cost savings.",
      bullets: [
        "Email & file migration (M365, Google Workspace)",
        "Hybrid AD & identity management",
        "IaaS (AWS/Azure) for workloads",
        "Cloud backup & DR replication",
      ],
    },
    {
      icon: <Wrench className="w-8 h-8 text-orange-400" />,
      title: "System Integration",
      desc: "Interconnecting your ICT stack for seamless workflows.",
      bullets: [
        "ERP & HR system integration",
        "API-based data flow automation",
        "Legacy modernization",
        "Custom ICT dashboards & reporting",
      ],
    },
  ];

  const configuration = [
    {
      title: "Infrastructure Planning",
      icon: <Laptop className="w-6 h-6 text-purple-400" />,
      points: [
        "Site survey & capacity planning",
        "Hardware/software compatibility mapping",
        "Redundancy & failover design",
      ],
    },
    {
      title: "Deployment & Setup",
      icon: <HardDrive className="w-6 h-6 text-blue-400" />,
      points: [
        "Server racking & cabling",
        "OS installation & hardening",
        "Network configuration & VLAN setup",
      ],
    },
    {
      title: "Integration & Security",
      icon: <Shield className="w-6 h-6 text-green-400" />,
      points: [
        "Active Directory / LDAP integration",
        "VPNs, firewalls, endpoint protection",
        "RBAC & least-privilege enforcement",
      ],
    },
    {
      title: "Documentation & Handover",
      icon: <FileCheck className="w-6 h-6 text-cyan-400" />,
      points: [
        "As-built documentation & diagrams",
        "User/admin training sessions",
        "Maintenance schedules & SLAs",
      ],
    },
  ];

  const support = [
    {
      title: "Helpdesk Support",
      points: [
        "Ticket-based support system",
        "Remote assistance & troubleshooting",
        "End-user guidance & knowledge base",
      ],
    },
    {
      title: "Managed Services",
      points: [
        "Proactive monitoring of servers & networks",
        "Patch management & security updates",
        "Capacity and performance reports",
      ],
    },
    {
      title: "SLAs & On-site Visits",
      points: [
        "Critical issues resolved within 2–4 hrs",
        "Quarterly site audits & preventive maintenance",
        "On-demand engineer dispatch",
      ],
    },
  ];

  const gallery = ["/images/ict/1.jpeg", "/images/ict/2.jpeg", "/images/ict/3.jpeg"];

  const tabs: { id: TabId; label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "solutions", label: "Solutions" },
    { id: "configuration", label: "Configuration" },
    { id: "support", label: "Support" },
    { id: "faq", label: "FAQ" },
    { id: "portfolio", label: "Portfolio" },
  ];

  const faqItems = [
    {
      q: "Do you provide both hardware and software?",
      a: "Yes, we handle full ICT procurement: servers, laptops, switches, plus software licensing and subscriptions.",
    },
    {
      q: "Can you integrate with our existing systems?",
      a: "Absolutely. We specialize in integration with ERPs, HRMS, and legacy systems to ensure smooth workflows.",
    },
    {
      q: "Do you offer managed services?",
      a: "Yes, our managed services cover proactive monitoring, patching, and user support.",
    },
    {
      q: "Which regions do you serve?",
      a: "We are based in Meru and Nairobi, with deployments across Kenya.",
    },
  ];

  const servicesJsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "Northstart Systems",
    url: "https://northstart.co.ke",
    areaServed: "Kenya",
    description,
    telephone: "+254700000000",
    makesOffer: [
      { "@type": "Service", name: "ICT Hardware Procurement" },
      { "@type": "Service", name: "Software Licensing" },
      { "@type": "Service", name: "Network Setup" },
      { "@type": "Service", name: "Server Installation" },
      { "@type": "Service", name: "Cloud Solutions" },
      { "@type": "Service", name: "Managed Support" },
    ],
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="pt-20 bg-gradient-to-br from-slate-200 via-blue-900 to-indigo-200 min-h-screen">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta
          name="keywords"
          content="ICT systems Kenya, ICT procurement, server installation, network setup Nairobi, cloud solutions, managed IT support Kenya"
        />
        <meta property="og:title" content={pageName + " | Northstart Systems"} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content="/images/ict/1.jpeg" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageName + " | Northstart Systems"} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content="/images/ict/1.jpeg" />
        <script type="application/ld+json">{JSON.stringify(servicesJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 flex gap-8">
        <Sidebar />

        <main className="flex-1 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="p-8 bg-gradient-to-r from-purple-500/20 to-indigo-500/20 border-b border-white/20">
            <Link to="/services" className="inline-flex items-center text-cyan-300 hover:text-white mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Services
            </Link>
            <h1 className="text-4xl font-bold mb-3">{pageName}</h1>
            <p className="text-lg text-slate-200 mb-6">{description}</p>
            <div className="flex flex-wrap gap-3">
              {heroBadges.map((b, i) => (
                <div key={i} className="flex items-center bg-white/10 px-3 py-2 rounded-full border border-white/20">
                  {b.icon}
                  <span className="text-slate-100 text-sm">{b.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="px-6 py-3 bg-white/5 border-b border-white/10 flex flex-wrap gap-2">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  activeTab === t.id
                    ? "bg-purple-500/30 text-white border border-purple-400/50"
                    : "bg-white/10 text-slate-300 hover:bg-white/20 hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="p-8 text-white">
            {activeTab === "overview" && (
              <section className="space-y-8">
                <h2 className="text-3xl font-bold">Why Choose Our ICT Systems?</h2>
                <p className="text-slate-300">
                  From hardware to cloud, we provide turnkey ICT infrastructure tailored for SMEs, NGOs, and enterprise.
                </p>
              </section>
            )}

            {activeTab === "solutions" && (
              <div className="space-y-8">
                <h2 className="text-3xl font-bold">Our ICT Solutions</h2>
                <div className="grid gap-8">
                  {solutions.map((s, i) => (
                    <div key={i} className="bg-white/5 rounded-2xl border border-white/10 p-8">
                      <div className="flex items-start gap-6">
                        <div className="p-4 bg-white/10 rounded-2xl">{s.icon}</div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-semibold mb-2">{s.title}</h3>
                          <p className="text-slate-300 mb-4">{s.desc}</p>
                          <ul className="grid md:grid-cols-2 gap-2">
                            {s.bullets.map((b, idx) => (
                              <li key={idx} className="flex items-start">
                                <CheckCircle className="w-4 h-4 text-emerald-400 mr-2 mt-0.5" />
                                <span className="text-slate-200 text-sm">{b}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "configuration" && (
              <div className="space-y-8">
                <h2 className="text-3xl font-bold">Installation & Configuration</h2>
                <div className="grid gap-8">
                  {configuration.map((c, i) => (
                    <div key={i} className="bg-white/5 rounded-2xl border border-white/10 p-8">
                      <div className="flex items-start gap-4 mb-4">
                        {c.icon}
                        <h3 className="text-xl font-semibold">{c.title}</h3>
                      </div>
                      <ul className="grid md:grid-cols-2 gap-2">
                        {c.points.map((p, idx) => (
                          <li key={idx} className="flex items-start">
                            <CheckCircle className="w-4 h-4 text-emerald-400 mr-2 mt-0.5" />
                            <span className="text-slate-200 text-sm">{p}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "support" && (
              <div className="space-y-8">
                <h2 className="text-3xl font-bold">Support & Managed Services</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  {support.map((s, i) => (
                    <div key={i} className="bg-white/5 rounded-2xl border border-white/10 p-6">
                      <h3 className="text-xl font-semibold mb-3">{s.title}</h3>
                      <ul className="space-y-2 text-sm">
                        {s.points.map((p, idx) => (
                          <li key={idx} className="flex items-start">
                            <CheckCircle className="w-4 h-4 text-emerald-400 mr-2 mt-0.5" />
                            <span className="text-slate-200">{p}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "faq" && (
              <div className="space-y-8">
                <h2 className="text-3xl font-bold">Frequently Asked Questions</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {faqItems.map((f, i) => (
                    <div key={i} className="bg-white/5 rounded-2xl border border-white/10 p-6">
                      <h3 className="text-lg font-semibold mb-2">{f.q}</h3>
                      <p className="text-slate-300 text-sm">{f.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "portfolio" && (
              <div className="space-y-8">
                <h2 className="text-3xl font-bold">ICT Projects Portfolio</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {gallery.map((img, idx) => (
                    <div key={idx} className="group relative overflow-hidden rounded-2xl">
                      <img
                        src={img}
                        alt={`${pageName} ${idx + 1}`}
                        className="w-full h-64 object-cover transition-transform group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="absolute bottom-4 left-4 right-4">
                          <h3 className="text-white font-semibold mb-1">ICT Project {idx + 1}</h3>
                          <p className="text-slate-200 text-sm">Infrastructure Deployment</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Contact Strip */}
                <div className="bg-white/5 rounded-2xl border border-white/10 p-6 grid sm:grid-cols-3 gap-6 text-center">
                  <div className="flex flex-col items-center">
                    <Phone className="w-7 h-7 text-cyan-400 mb-2" />
                    <p className="text-slate-200">+254 700 041385</p>
                  </div>
                  <div className="flex flex-col items-center">
                    <Mail className="w-7 h-7 text-emerald-400 mb-2" />
                    <p className="text-slate-200">info@northstartsystems.com</p>
                  </div>
                  <div className="flex flex-col items-center">
                    <MapPin className="w-7 h-7 text-purple-400 mb-2" />
                    <p className="text-slate-200">Riara Valley Garden, Apartment C8, 3rd Floor, Off Riara Road Kilimani, Nairobi, Kenya</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ICTSystems;
